import { RelativeTimeUnits } from 'pounceV2';
import dayjs from 'dayjs';
import uniqBy from 'lodash/uniqBy';

import { CrossLogSearchFormValues, TimeFrameEnum } from '../../../../types';
import { Database } from '../../../components/FiltersSection/hooks/useDatabases';
import { Table } from '../../../components/FiltersSection/hooks/useGetTables';
import { updatePantherFlowQueryTextUpdates } from './useUpdatePantherFlowQueryTextFromFormValues';
import { ALL_ARRAY } from '../constants';

const INVALID_TIME_FRAMES = [
  TimeFrameEnum.AllTime,
  TimeFrameEnum.Custom,
  TimeFrameEnum.RelativeTimeCustom,
];

export const getISO8601DurationFromCustomRelativeTime = (
  relativeNumberValue: number,
  relativeTimeUnit: RelativeTimeUnits,
): string => {
  switch (relativeTimeUnit) {
    case RelativeTimeUnits.Mins:
      return `PT${relativeNumberValue}M`;
    case RelativeTimeUnits.Hours:
      return `PT${relativeNumberValue}H`;
    case RelativeTimeUnits.Days:
      return `P${relativeNumberValue}D`;
    case RelativeTimeUnits.Weeks:
      return `P${relativeNumberValue}W`;
    case RelativeTimeUnits.Months:
      return `P${relativeNumberValue}M`;
    case RelativeTimeUnits.Years:
      return `P${relativeNumberValue}Y`;
    default:
      return `PT${relativeNumberValue}M`;
  }
};

const CUSTOM_TIME_FRAME_FORMAT = 'YYYY-MM-DD[T]HH:mm:ss[Z]';

export const getTimeStringsFromValues = (
  values: CrossLogSearchFormValues,
): { startTimeString: string; endTimeString: string } => {
  // Handle timeFrame-based scenarios
  if (values.timeFrame) {
    // If the time frame isn't "All Time", "Custom", or "Relative Time Custom", we want to use the time frame value
    if (!INVALID_TIME_FRAMES.includes(values.timeFrame)) {
      return { startTimeString: values.timeFrame, endTimeString: '' };
    }

    // If the time frame is "All Time", we want to clear the start and end time values
    if (values.timeFrame === TimeFrameEnum.AllTime) {
      return { startTimeString: '', endTimeString: '' };
    }

    // If the time frame is "Relative Time Custom", we want to use the relative number and time unit values
    if (values.timeFrame === TimeFrameEnum.RelativeTimeCustom) {
      const { relativeNumberVal, relativeTimeUnit } = values;
      if (relativeTimeUnit && relativeNumberVal) {
        return {
          startTimeString: getISO8601DurationFromCustomRelativeTime(
            relativeNumberVal,
            relativeTimeUnit,
          ),
          endTimeString: '',
        };
      }
    }
  }

  // Handle custom absolute time range values
  let startTimeString = null;
  let endTimeString = null;

  if (values.startTime && (dayjs.isDayjs(values.startTime) || dayjs(values.startTime).isValid())) {
    // values.startTime can look like "Thu Mar 06 2025 15:02:59 GMT-0800 (Pacific Standard Time)" or "2025-03-05T23:02:00Z",
    // so we need to convert it to a UTC time and then format it
    startTimeString = dayjs(values.startTime).utc().format(CUSTOM_TIME_FRAME_FORMAT);
  }

  if (values.endTime && (dayjs.isDayjs(values.endTime) || dayjs(values.endTime).isValid())) {
    // values.endTime can look like "Thu Mar 06 2025 15:02:59 GMT-0800 (Pacific Standard Time)" or "2025-03-05T23:02:00Z",
    // so we need to convert it to a UTC time and then format it
    endTimeString = dayjs(values.endTime).utc().format(CUSTOM_TIME_FRAME_FORMAT);
  }

  return { startTimeString, endTimeString };
};

export const getStartAndEndTimeStringsFromUpdates = (
  updates: updatePantherFlowQueryTextUpdates,
): { startTimeString: string; endTimeString: string } => {
  if (updates.startTime === TimeFrameEnum.AllTime || updates.startTime === TimeFrameEnum.Custom) {
    return {
      startTimeString: '',
      endTimeString: '',
    };
  }

  return {
    startTimeString: updates.startTime,
    endTimeString: updates.endTime,
  };
};

export const getStartAndEndTimeStrings = (
  updates: updatePantherFlowQueryTextUpdates,
  values: CrossLogSearchFormValues,
): { startTimeString: string; endTimeString: string } => {
  if (updates.startTime) {
    return getStartAndEndTimeStringsFromUpdates(updates);
  }

  return getTimeStringsFromValues(values);
};

export const isAllSelected = (
  selectedItems: string[],
  totalItems: Database[] | Table[],
): boolean => {
  return !selectedItems?.length || selectedItems?.length === totalItems?.length;
};

export const getAllTablesForSelectedDatabases = (
  selectedDatabases: string[],
  allDatabases: Database[],
): Table[] => {
  // If no databases are selected, use all databases
  const selectedDatabaseNames = selectedDatabases.length
    ? selectedDatabases
    : allDatabases.map(db => db.name);

  // Get all tables for the selected databases
  const tablesForSelectedDatabases = selectedDatabaseNames.reduce((acc, db) => {
    const database = allDatabases.find(d => d.name === db);
    return [...acc, ...(database?.tables || [])];
  }, []);

  // Remove duplicates - it's possible for the same table to exist in multiple databases
  const uniqueTables = uniqBy(tablesForSelectedDatabases, 'name');

  return uniqueTables.map(table => table.name);
};

export const getDatabaseTableInfo = ({
  updates,
  values,
  allDatabases,
}: {
  updates: updatePantherFlowQueryTextUpdates;
  values: CrossLogSearchFormValues;
  allDatabases: Database[];
}): { databases: string[]; tables: string[] } => {
  const databases = updates?.databases || values.databases || [];
  const tables = updates?.tables || values.tables || [];

  const allTablesForSelectedDatabases = getAllTablesForSelectedDatabases(databases, allDatabases);

  let databasesToUse = isAllSelected(databases, allDatabases) ? ALL_ARRAY : databases;
  let tablesToUse = isAllSelected(tables, allTablesForSelectedDatabases) ? ALL_ARRAY : tables;

  // We set the databases and tables to null if the user manually enters a value that's unsyncable. We
  // set both to null if either is null in order to support the ability to still update the time range.
  // Passing in null for both databases and tables will allow just the time range to be updated.
  if (
    (values.databases === null && !updates?.databases) ||
    (values.tables === null && !updates?.tables)
  ) {
    databasesToUse = null;
    tablesToUse = null;
  }

  const databaseTableInfo = {
    databases: databasesToUse,
    tables: tablesToUse,
  };

  return databaseTableInfo;
};
